import { User } from '@/types';

const STORAGE_KEYS = {
  AUTH: 'vitrineturbo_auth',
  USER: 'vitrineturbo_user',
  APP_STATE: 'vitrineturbo_state',
} as const;

interface StoredAuth {
  email: string;
  password: string;
  timestamp: number;
}

interface AppState {
  lastSync: number;
  currentView?: string;
  filters?: Record<string, any>;
}

export function storeAuth(email: string, password: string) {
  try {
    const auth: StoredAuth = {
      email,
      password,
      timestamp: Date.now(),
    };
    localStorage.setItem(STORAGE_KEYS.AUTH, JSON.stringify(auth));
    
    // Broadcast para outras abas
    window.dispatchEvent(new StorageEvent('storage', {
      key: STORAGE_KEYS.AUTH,
      newValue: JSON.stringify(auth),
    }));
  } catch (error) {
    console.error('Error storing auth:', error);
    throw new Error('Failed to store auth data');
  }
}

export function getStoredAuth(): StoredAuth | null {
  try {
    const auth = localStorage.getItem(STORAGE_KEYS.AUTH);
    return auth ? JSON.parse(auth) : null;
  } catch (error) {
    console.error('Error getting stored auth:', error);
    return null;
  }
}

export function storeUser(user: User) {
  try {
    localStorage.setItem(STORAGE_KEYS.USER, JSON.stringify(user));
    
    // Broadcast para outras abas
    window.dispatchEvent(new StorageEvent('storage', {
      key: STORAGE_KEYS.USER,
      newValue: JSON.stringify(user),
    }));
  } catch (error) {
    console.error('Error storing user:', error);
    throw new Error('Failed to store user data');
  }
}

export function getStoredUser(): User | null {
  try {
    const user = localStorage.getItem(STORAGE_KEYS.USER);
    return user ? JSON.parse(user) : null;
  } catch (error) {
    console.error('Error getting stored user:', error);
    return null;
  }
}

export function storeAppState(state: Partial<AppState>) {
  try {
    const currentState = getAppState();
    const newState = {
      ...currentState,
      ...state,
      lastSync: Date.now(),
    };
    localStorage.setItem(STORAGE_KEYS.APP_STATE, JSON.stringify(newState));
    
    // Broadcast para outras abas
    window.dispatchEvent(new StorageEvent('storage', {
      key: STORAGE_KEYS.APP_STATE,
      newValue: JSON.stringify(newState),
    }));
  } catch (error) {
    console.error('Error storing app state:', error);
    throw new Error('Failed to store app state');
  }
}

export function getAppState(): AppState {
  try {
    const state = localStorage.getItem(STORAGE_KEYS.APP_STATE);
    return state ? JSON.parse(state) : { lastSync: Date.now() };
  } catch (error) {
    console.error('Error getting app state:', error);
    return { lastSync: Date.now() };
  }
}

export function clearAllData() {
  try {
    Object.values(STORAGE_KEYS).forEach(key => {
      localStorage.removeItem(key);
    });
    
    // Broadcast para outras abas
    window.dispatchEvent(new StorageEvent('storage', {
      key: STORAGE_KEYS.AUTH,
      newValue: null,
    }));
  } catch (error) {
    console.error('Error clearing data:', error);
    throw new Error('Failed to clear data');
  }
}

export function initStorageSync(
  onAuthChange: (auth: StoredAuth | null) => void,
  onUserChange: (user: User | null) => void
) {
  window.addEventListener('storage', (event) => {
    if (!event.key) return;

    try {
      switch (event.key) {
        case STORAGE_KEYS.AUTH:
          onAuthChange(event.newValue ? JSON.parse(event.newValue) : null);
          break;
        case STORAGE_KEYS.USER:
          onUserChange(event.newValue ? JSON.parse(event.newValue) : null);
          break;
        case STORAGE_KEYS.APP_STATE:
          if (event.newValue) {
            const newState = JSON.parse(event.newValue);
            const currentState = getAppState();
            if (newState.lastSync > currentState.lastSync) {
              localStorage.setItem(STORAGE_KEYS.APP_STATE, event.newValue);
            }
          }
          break;
      }
    } catch (error) {
      console.error('Error handling storage event:', error);
    }
  });

  window.addEventListener('focus', () => {
    const currentAuth = getStoredAuth();
    if (currentAuth) {
      onAuthChange(currentAuth);
    }
    
    const currentUser = getStoredUser();
    if (currentUser) {
      onUserChange(currentUser);
    }
  });

  window.addEventListener('beforeunload', () => {
    storeAppState({ lastSync: Date.now() });
  });
}